/**
 * When the PIN pad signs in by itself (SCOPE §12.5, decision 138).
 *
 * It used to sign in on the fourth digit, always — while Staff let a PIN be
 * anything from four to eight. A six-digit PIN was sent after four, refused,
 * cleared, and counted against the till's ten-wrong lockout: that person could
 * never sign in, and every try brought the till closer to locking everybody out.
 *
 * Now the pad signs in on the last digit only when it knows how many there
 * are. When it does not — a PIN set before lengths were recorded — it takes up
 * to eight and waits for Sign in or Enter.
 */

export const PIN_MIN_DIGITS = 4;
export const PIN_MAX_DIGITS = 8;

export interface PinPadRules {
  /** No more digits than this are taken. */
  readonly maxDigits: number;
  /** Sign in on this many digits; null waits for Sign in or Enter. */
  readonly submitsAt: number | null;
  /** How many places to draw once `typed` digits are in. */
  readonly places: (typed: number) => number;
  /**
   * Why Sign in cannot be pressed yet, or undefined when it can. Where the
   * length is known, nothing shorter is sent: a six-digit store's pad let
   * *Sign in* go at four, which sent a PIN that could never match and spent
   * one of the till's ten tries (2026-09-30).
   */
  readonly submitReason: (typed: number) => string | undefined;
}

/** @param length The PIN's length when known — the person's, or the store's on a pad with no names. */
export function pinPadRules(length: number | null): PinPadRules {
  if (length === null) {
    return {
      maxDigits: PIN_MAX_DIGITS,
      submitsAt: null,
      places: (typed) => Math.max(PIN_MIN_DIGITS, typed),
      submitReason: (typed) =>
        typed < PIN_MIN_DIGITS ? `Enter at least ${PIN_MIN_DIGITS} digits` : undefined,
    };
  }
  return {
    maxDigits: length,
    submitsAt: length,
    places: () => length,
    submitReason: (typed) => (typed < length ? `Enter all ${length} digits` : undefined),
  };
}
